import { supabase } from '../lib/supabase';
import { Notification } from '../types';

export interface CreateNotificationParams {
  title: string;
  message: string;
  type: 'production' | 'sample' | 'express' | 'express_sample' | 'system' | 'crm_reminder';
  entity_type: 'production' | 'sample' | 'express' | 'express_sample' | 'system' | 'crm_reminder';
  entity_id: string | null;
  action_url?: string;
}

export class NotificationService {
  private static readonly ADMIN_MONITOR_EMAIL = 'renzocamporesi@gmail.com';
  private static secondaryEmailCache: Record<string, string | null> = {};

  private static async getSecondaryEmail(userId: string): Promise<string | null> {
    if (userId in this.secondaryEmailCache) {
      const cached = this.secondaryEmailCache[userId];
      console.log(`[NotificationService] Using cached secondary email for user ${userId}: ${cached || 'none'}`);
      return cached;
    }
    try {
      console.log(`[NotificationService] Fetching secondary email for user ${userId} from database...`);
      const { data, error } = await supabase
        .from('users')
        .select('secondary_email, email')
        .eq('id', userId)
        .maybeSingle();
      if (error) {
        console.error(`[NotificationService] Error fetching secondary email for user ${userId}:`, error);
        this.secondaryEmailCache[userId] = null;
        return null;
      }
      if (!data) {
        console.warn(`[NotificationService] No user record found in public.users for user ${userId}`);
        this.secondaryEmailCache[userId] = null;
        return null;
      }
      const value = (data?.secondary_email || null) as string | null;
      this.secondaryEmailCache[userId] = value;
      console.log(`[NotificationService] Found secondary email for user ${userId} (${data.email}): ${value || 'none'}`);
      return value;
    } catch (e) {
      console.error(`[NotificationService] Unexpected error fetching secondary email for user ${userId}:`, e);
      this.secondaryEmailCache[userId] = null;
      return null;
    }
  }

  // Método para limpiar la caché cuando se actualiza el email secundario
  static clearSecondaryEmailCache(userId?: string): void {
    if (userId) {
      delete this.secondaryEmailCache[userId];
    } else {
      this.secondaryEmailCache = {};
    }
  }

  private static buildRecipients(args: { primaryEmail: string; secondaryEmail?: string | null; role?: string; alwaysIncludeAdminMonitor?: boolean }): string[] {
    const recipients = new Set<string>();
    const primary = args.primaryEmail?.trim();
    const secondary = args.secondaryEmail?.trim();

    if (primary) {
      recipients.add(primary);
    }
    if (secondary) {
      recipients.add(secondary);
    }
    
    // Si el rol es Administrador O si se solicita explícitamente, agregar el email de monitoreo
    if (args.role === 'Administrador' || args.alwaysIncludeAdminMonitor) {
      console.log(`[NotificationService] Adding admin monitor email for role: ${args.role}, alwaysInclude: ${args.alwaysIncludeAdminMonitor}`);
      recipients.add(this.ADMIN_MONITOR_EMAIL);
    }

    const result = Array.from(recipients);
    console.log(`[NotificationService] buildRecipients result for role ${args.role}:`, result);
    return result;
  }
  static async getUsersWithRoles(): Promise<any[]> {
    const { data: users } = await supabase.rpc('get_users_with_roles');
    return users || [];
  }

  static async notifyAdmins(params: CreateNotificationParams): Promise<void> {
    try {
      const users = await this.getUsersWithRoles();
      const adminUsers = users.filter(u => u.app_role === 'Administrador' || u.app_role === 'Super Admin');
      if (!adminUsers.length) return;
      await this.notifyUsers(adminUsers.map((u:any)=>u.id), params);
    } catch (error) {
      console.error('Error in notifyAdmins:', error);
    }
  }

  static async notifyPaymentReceiptUploaded(params: {
    entityType: 'production' | 'sample' | 'express' | 'express_sample';
    entityId: string | null;
    entityName: string;
    clientName: string;
    receiptUrl: string;
    salesperson: string;
    title: string;
    message: string;
  }): Promise<void> {
    const notificationType = params.entityType === 'express_sample' ? 'express_sample' : params.entityType;
    await this.notifyAdmins({
      title: params.title,
      message: params.message,
      type: notificationType as CreateNotificationParams['type'],
      entity_type: notificationType as CreateNotificationParams['entity_type'],
      entity_id: params.entityId,
      action_url: params.receiptUrl,
    });
  }

  static async notifyProductionEvent(params: CreateNotificationParams): Promise<void> {
    try {
      const users = await this.getUsersWithRoles();
      const prodUsers = users.filter(u => u.app_role === 'Producción');
      if (!prodUsers.length) return;
      await this.notifyUsers(prodUsers.map((u:any)=>u.id), params);
    } catch (error) {
      console.error('Error in notifyProductionEvent:', error);
    }
  }
  /**
   * Envía notificaciones a todos los usuarios con rol de producción
   */
  static async notifyProductionUsers(params: CreateNotificationParams): Promise<void> {
    try {
      // Obtener todos los usuarios con roles usando la función RPC
      const { data: users, error: usersError } = await supabase
        .rpc('get_users_with_roles');

      if (usersError) {
        console.error('Error fetching users with roles:', usersError);
        return;
      }

      // Filtrar usuarios con rol 'Producción' O 'Administrador'
      const targetUsers = users?.filter(user => 
        ['Producción', 'Administrador'].includes(user.app_role)
      ) || [];

      if (targetUsers.length === 0) {
        console.log('No target users found (Producción, Administrador)');
        return;
      }

      console.log(`Found ${targetUsers.length} users to notify:`, 
        targetUsers.map(u => ({ id: u.id, role: u.app_role }))
      );

      // Verificar preferencias de notificaciones para cada usuario
      const notificationsToCreate: Omit<Notification, 'id' | 'created_at' | 'updated_at'>[] = [];

      for (const user of targetUsers) {
        try {
          // Verificar si el usuario tiene preferencias habilitadas para este tipo
          const { data: preferences } = await supabase
            .from('notification_preferences')
            .select('*')
            .eq('user_id', user.id)
            .maybeSingle();

          let shouldNotify = true; // Default to true

          if (preferences) {
            // Check specific preferences
            switch (params.type) {
              case 'production':
                shouldNotify = preferences.production_notifications;
                break;
              case 'sample':
                shouldNotify = preferences.sample_notifications;
                break;
              case 'express':
                shouldNotify = preferences.express_notifications;
                break;
              case 'express_sample':
                shouldNotify = preferences.express_sample_notifications;
                break;
              case 'crm_reminder':
                shouldNotify = preferences.system_notifications; // Usar system_notifications para CRM
                break;
              case 'system':
                shouldNotify = preferences.system_notifications;
                break;
            }
          }

          if (shouldNotify) {
            notificationsToCreate.push({
              user_id: user.id,
              title: params.title,
              message: params.message,
              type: params.type,
              entity_type: params.entity_type,
              entity_id: params.entity_id,
              is_read: false,
              action_url: params.action_url,
            });
          }
        } catch (userError) {
          console.error(`Error processing user ${user.id}:`, userError);
          // Continue with other users
        }
      }

      // Crear todas las notificaciones
      if (notificationsToCreate.length > 0) {
        const { error: createError } = await supabase
          .from('notifications')
          .insert(notificationsToCreate);

        if (createError) {
          console.error('Error creating notifications:', createError);
        } else {
          console.log(`Created ${notificationsToCreate.length} notifications for production users`);
        }
      }
    } catch (error) {
      console.error('Error in notifyProductionUsers:', error);
    }
  }

  /**
   * Notifica cuando se crea una nueva producción
   */
  static async notifyNewProduction(productionId: string, productionName: string, clientName: string): Promise<void> {
    await this.notifyProductionUsers({
      title: 'Nueva Producción Creada',
      message: `Se ha creado una nueva producción: "${productionName}" para el cliente "${clientName}"`,
      type: 'production',
      entity_type: 'production',
      entity_id: productionId,
      action_url: `/productions`,
    });

    try {
      // Obtener el usuario actual que creó la producción
      const { data: currentUser } = await supabase.auth.getUser();
      const creatorEmail = currentUser?.user?.email || 'Sistema';
      const creatorName = creatorEmail.split('@')[0];

      const { data: production } = await supabase
        .from('productions')
        .select('name, client_id, delivery_date, status, ficha_number, created_at')
        .eq('id', productionId)
        .maybeSingle();

      if (!production) return;

      const { data: client } = await supabase
        .from('clients')
        .select('name')
        .eq('id', production.client_id)
        .maybeSingle();

      const users = await this.getUsersWithRoles();
      const prodUsers = users.filter(u =>
        ['Producción', 'Administrador'].includes(u.app_role)
      );
      
      // Verificar si hay administradores en la lista
      const hasAdmins = prodUsers.some(u => u.app_role === 'Administrador');
      console.log(`[NotificationService] Production users: ${prodUsers.length}, Has admins: ${hasAdmins}`);

      // Buscar template específico o usar uno genérico
      let { data: template } = await supabase
        .from('email_templates')
        .select('id')
        .eq('name', 'production_created')
        .eq('is_active', true)
        .maybeSingle();

      // Si no existe el template específico, buscar uno genérico
      if (!template) {
        console.warn('[NotificationService] Template "production_created" not found, trying "system_notification"');
        const { data: systemTemplate } = await supabase
          .from('email_templates')
          .select('id')
          .eq('name', 'system_notification')
          .eq('is_active', true)
          .maybeSingle();
        template = systemTemplate;
      }

      // Si aún no hay template, continuar sin template (template_id puede ser null)
      if (!template) {
        console.warn('[NotificationService] No email template found, continuing without template');
      }

      const statusLabels: Record<string, string> = {
        'draft': 'Borrador',
        'pending': 'Pendiente',
        'in_progress': 'En Progreso',
        'finished': 'Finalizada'
      };

      for (const user of prodUsers) {
        console.log(`[NotificationService] Processing user: ${user.email || 'no email'} (id: ${user.id}, role: ${user.app_role})`);
        
        // Verificar si el usuario existe en public.users y obtener su email real
        const { data: userRecord, error: userRecordError } = await supabase
          .from('users')
          .select('id, email, secondary_email')
          .eq('id', user.id)
          .maybeSingle();
        
        if (userRecordError) {
          console.error(`[NotificationService] Error fetching user record for ${user.id}:`, userRecordError);
          continue; // Saltar este usuario si hay error
        }
        
        if (!userRecord) {
          console.warn(`[NotificationService] User record not found in public.users for id: ${user.id}`);
          continue; // Saltar este usuario si no existe en public.users
        }
        
        console.log(`[NotificationService] User record in public.users:`, userRecord);
        
        // Usar el email del registro de users, no el de getUsersWithRoles
        const primaryEmail = userRecord.email || user.email;
        if (!primaryEmail) {
          console.warn(`[NotificationService] No email found for user ${user.id}, skipping`);
          continue;
        }
        
        // Obtener secondary_email y limpiarlo (trim, convertir vacío a null)
        let secondary: string | null = null;
        if (userRecord?.secondary_email) {
          const cleaned = userRecord.secondary_email.trim();
          secondary = cleaned.length > 0 ? cleaned : null;
        }
        
        console.log(`[NotificationService] Secondary email for ${primaryEmail}: ${secondary || 'none'} (raw: ${userRecord?.secondary_email || 'null'})`);
        
        // Crear lista de recipients: siempre incluir el email principal, y el secondary si existe
        // Si hay administradores en la lista, siempre incluir el email de monitoreo
        console.log(`[NotificationService] Building recipients for user ${primaryEmail} with role: ${user.app_role}`);
        const recipients = this.buildRecipients({
          primaryEmail: primaryEmail,
          secondaryEmail: secondary,
          role: user.app_role,
          alwaysIncludeAdminMonitor: hasAdmins // Incluir email de monitoreo si hay administradores
        });
        
        console.log(`[NotificationService] Recipients list:`, recipients);
        console.log(`[NotificationService] Sending production email to user ${user.id} (${primaryEmail}): primary=${primaryEmail}, secondary=${secondary || 'none'}, role=${user.app_role}, total recipients=${recipients.length}`, recipients);
        
        for (const recipient of recipients) {
          console.log(`[NotificationService] Inserting email queue entry for: ${recipient}`);
          const insertPayload: any = {
            user_id: user.id, // Campo requerido
            email: recipient,
            template_id: template?.id || null, // Puede ser null si no hay template
            template_data: {
              created_by: creatorName,
              created_by_email: creatorEmail,
              production_name: production.name,
              client_name: client?.name || 'Desconocido',
              created_date: new Date(production.created_at).toLocaleDateString('es-AR'),
              delivery_date: production.delivery_date ? new Date(production.delivery_date).toLocaleDateString('es-AR') : 'No definida',
              status: statusLabels[production.status] || production.status,
              ficha_number: production.ficha_number || 'Sin número',
              notification_type: 'production_created'
            },
            status: 'pending'
          };
          
          console.log(`[NotificationService] Insert payload:`, insertPayload);
          
          // Usar función RPC para insertar (evita problemas de RLS)
          const { error: insertError, data: insertData } = await supabase.rpc('insert_email_notification_queue', {
            p_user_id: insertPayload.user_id,
            p_email: insertPayload.email,
            p_template_id: insertPayload.template_id,
            p_template_data: insertPayload.template_data,
            p_notification_id: insertPayload.notification_id || null,
            p_status: insertPayload.status
          });
            
          if (insertError) {
            console.error(`[NotificationService] ❌ Error inserting email for ${recipient}:`, insertError);
            console.error(`[NotificationService] Error details:`, JSON.stringify(insertError, null, 2));
          } else {
            console.log(`[NotificationService] ✅ Email queued successfully for ${recipient}`, insertData);
          }
        }
      }
    } catch (error) {
      console.error('Error sending production email:', error);
    }
  }

  /**
   * Notifica cuando se crea una nueva muestra
   */
  static async notifyNewSample(sampleId: string, sampleName: string, clientName: string): Promise<void> {
    await this.notifyProductionUsers({
      title: 'Nueva Muestra Creada',
      message: `Se ha creado una nueva muestra: "${sampleName}" para el cliente "${clientName}"`,
      type: 'sample',
      entity_type: 'sample',
      entity_id: sampleId,
      action_url: `/samples`,
    });

    try {
      // Obtener el usuario actual que creó la muestra
      const { data: currentUser } = await supabase.auth.getUser();
      const creatorEmail = currentUser?.user?.email || 'Sistema';
      const creatorName = creatorEmail.split('@')[0];

      const { data: sample } = await supabase
        .from('samples')
        .select('name, client_id, delivery_date, status, created_at')
        .eq('id', sampleId)
        .maybeSingle();

      if (!sample) return;

      const { data: client } = await supabase
        .from('clients')
        .select('name')
        .eq('id', sample.client_id)
        .maybeSingle();

      const users = await this.getUsersWithRoles();
      console.log(`[NotificationService] Total users from getUsersWithRoles: ${users.length}`);
      const prodUsers = users.filter(u =>
        ['Producción', 'Administrador'].includes(u.app_role)
      );
      console.log(`[NotificationService] Production users to notify: ${prodUsers.length}`, prodUsers.map(u => ({ id: u.id, email: u.email, role: u.app_role })));
      
      // Verificar si hay administradores en la lista
      const hasAdmins = prodUsers.some(u => u.app_role === 'Administrador');
      console.log(`[NotificationService] Has admins: ${hasAdmins}`);

      // Buscar template específico o usar uno genérico
      let { data: template } = await supabase
        .from('email_templates')
        .select('id')
        .eq('name', 'sample_created')
        .eq('is_active', true)
        .maybeSingle();

      // Si no existe el template específico, buscar uno genérico
      if (!template) {
        console.warn('[NotificationService] Template "sample_created" not found, trying "system_notification"');
        const { data: systemTemplate } = await supabase
          .from('email_templates')
          .select('id')
          .eq('name', 'system_notification')
          .eq('is_active', true)
          .maybeSingle();
        template = systemTemplate;
      }

      // Si aún no hay template, continuar sin template (template_id puede ser null)
      if (!template) {
        console.warn('[NotificationService] No email template found, continuing without template');
      }

      const statusLabels: Record<string, string> = {
        'draft': 'Borrador',
        'pending': 'Pendiente',
        'in_progress': 'En Progreso',
        'finished': 'Finalizada',
        'shipped': 'Enviada'
      };

      for (const user of prodUsers) {
        console.log(`[NotificationService] Processing user: ${user.email || 'no email'} (id: ${user.id}, role: ${user.app_role})`);
        
        // Verificar si el usuario existe en public.users y obtener su email real
        const { data: userRecord, error: userRecordError } = await supabase
          .from('users')
          .select('id, email, secondary_email')
          .eq('id', user.id)
          .maybeSingle();
        
        if (userRecordError) {
          console.error(`[NotificationService] Error fetching user record for ${user.id}:`, userRecordError);
          continue; // Saltar este usuario si hay error
        }
        
        if (!userRecord) {
          console.warn(`[NotificationService] User record not found in public.users for id: ${user.id}`);
          continue; // Saltar este usuario si no existe en public.users
        }
        
        console.log(`[NotificationService] User record in public.users:`, userRecord);
        
        // Usar el email del registro de users, no el de getUsersWithRoles
        const primaryEmail = userRecord.email || user.email;
        if (!primaryEmail) {
          console.warn(`[NotificationService] No email found for user ${user.id}, skipping`);
          continue;
        }
        
        // Obtener secondary_email y limpiarlo (trim, convertir vacío a null)
        let secondary: string | null = null;
        if (userRecord?.secondary_email) {
          const cleaned = userRecord.secondary_email.trim();
          secondary = cleaned.length > 0 ? cleaned : null;
        }
        
        console.log(`[NotificationService] Secondary email for ${primaryEmail}: ${secondary || 'none'} (raw: ${userRecord?.secondary_email || 'null'})`);
        
        // Crear lista de recipients: siempre incluir el email principal, y el secondary si existe
        // Si hay administradores en la lista, siempre incluir el email de monitoreo
        console.log(`[NotificationService] Building recipients for user ${primaryEmail} with role: ${user.app_role}`);
        const recipients = this.buildRecipients({
          primaryEmail: primaryEmail,
          secondaryEmail: secondary,
          role: user.app_role,
          alwaysIncludeAdminMonitor: hasAdmins // Incluir email de monitoreo si hay administradores
        });
        
        console.log(`[NotificationService] Recipients list:`, recipients);
        console.log(`[NotificationService] Sending sample email to user ${user.id} (${primaryEmail}): primary=${primaryEmail}, secondary=${secondary || 'none'}, role=${user.app_role}, total recipients=${recipients.length}`, recipients);
        
        for (const recipient of recipients) {
          console.log(`[NotificationService] Inserting email queue entry for: ${recipient}`);
          const insertPayload: any = {
            user_id: user.id, // Campo requerido
            email: recipient,
            template_id: template?.id || null, // Puede ser null si no hay template
            template_data: {
              created_by: creatorName,
              created_by_email: creatorEmail,
              sample_name: sample.name,
              client_name: client?.name || 'Desconocido',
              created_date: new Date(sample.created_at).toLocaleDateString('es-AR'),
              delivery_date: sample.delivery_date ? new Date(sample.delivery_date).toLocaleDateString('es-AR') : 'No definida',
              status: statusLabels[sample.status] || sample.status,
              notification_type: 'sample_created'
            },
            status: 'pending'
          };
          
          console.log(`[NotificationService] Insert payload:`, insertPayload);
          
          // Usar función RPC para insertar (evita problemas de RLS)
          const { error: insertError, data: insertData } = await supabase.rpc('insert_email_notification_queue', {
            p_user_id: insertPayload.user_id,
            p_email: insertPayload.email,
            p_template_id: insertPayload.template_id,
            p_template_data: insertPayload.template_data,
            p_notification_id: insertPayload.notification_id || null,
            p_status: insertPayload.status
          });
            
          if (insertError) {
            console.error(`[NotificationService] ❌ Error inserting email for ${recipient}:`, insertError);
            console.error(`[NotificationService] Error details:`, JSON.stringify(insertError, null, 2));
          } else {
            console.log(`[NotificationService] ✅ Email queued successfully for ${recipient}`, insertData);
          }
        }
      }
      
      // No procesar inmediatamente - dejar que el procesador automático lo haga
      // para evitar duplicados y problemas de concurrencia
    } catch (error) {
      console.error('Error sending sample email:', error);
    }
  }

  /**
   * Notifica cuando se crea un nuevo express
   */
  static async notifyNewExpress(expressId: string, expressName: string, clientName: string): Promise<void> {
    await this.notifyProductionUsers({
      title: 'Nuevo Express Creado',
      message: `Se ha creado un nuevo pedido de express: "${expressName}" para el cliente "${clientName}"`,
      type: 'express',
      entity_type: 'express',
      entity_id: expressId,
      action_url: `/express`,
    });

    try {
      // Obtener el usuario actual que creó el express
      const { data: currentUser } = await supabase.auth.getUser();
      const creatorEmail = currentUser?.user?.email || 'Sistema';
      const creatorName = creatorEmail.split('@')[0];

      const { data: express } = await supabase
        .from('express')
        .select('name, client_id, delivery_date, status, created_at')
        .eq('id', expressId)
        .maybeSingle();

      if (!express) return;

      const { data: client } = await supabase
        .from('clients')
        .select('name')
        .eq('id', express.client_id)
        .maybeSingle();

      const { data: items } = await supabase
        .from('express_items')
        .select('quantity')
        .eq('express_id', expressId);

      const totalQuantity = items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;

      const users = await this.getUsersWithRoles();
      const prodUsers = users.filter(u =>
        ['Producción', 'Administrador'].includes(u.app_role)
      );
      
      // Verificar si hay administradores en la lista
      const hasAdmins = prodUsers.some(u => u.app_role === 'Administrador');
      console.log(`[NotificationService] Express users: ${prodUsers.length}, Has admins: ${hasAdmins}`);

      // Buscar template específico o usar uno genérico
      let { data: template } = await supabase
        .from('email_templates')
        .select('id')
        .eq('name', 'express_created')
        .eq('is_active', true)
        .maybeSingle();

      // Si no existe el template específico, buscar uno genérico
      if (!template) {
        console.warn('[NotificationService] Template "express_created" not found, trying "system_notification"');
        const { data: systemTemplate } = await supabase
          .from('email_templates')
          .select('id')
          .eq('name', 'system_notification')
          .eq('is_active', true)
          .maybeSingle();
        template = systemTemplate;
      }

      // Si aún no hay template, continuar sin template (template_id puede ser null)
      if (!template) {
        console.warn('[NotificationService] No email template found, continuing without template');
      }

      const statusLabels: Record<string, string> = {
        'draft': 'Borrador',
        'pending': 'Pendiente',
        'in_progress': 'En Progreso',
        'finished': 'Finalizado',
        'shipped': 'Enviado'
      };

      for (const user of prodUsers) {
        // Obtener secondary_email directamente de la tabla users
        const { data: userRecord, error: userRecordError } = await supabase
          .from('users')
          .select('id, email, secondary_email')
          .eq('id', user.id)
          .maybeSingle();
        
        if (userRecordError) {
          console.error(`[NotificationService] Error fetching user record for ${user.id}:`, userRecordError);
          continue;
        }
        
        if (!userRecord) {
          console.warn(`[NotificationService] User record not found in public.users for id: ${user.id}`);
          continue;
        }
        
        // Usar el email del registro de users, no el de getUsersWithRoles
        const primaryEmail = userRecord.email || user.email;
        if (!primaryEmail) {
          console.warn(`[NotificationService] No email found for user ${user.id}, skipping`);
          continue;
        }
        
        // Obtener secondary_email y limpiarlo (trim, convertir vacío a null)
        let secondary: string | null = null;
        if (userRecord?.secondary_email) {
          const cleaned = userRecord.secondary_email.trim();
          secondary = cleaned.length > 0 ? cleaned : null;
        }
        
        // Crear lista de recipients: siempre incluir el email principal, y el secondary si existe
        // Si hay administradores en la lista, siempre incluir el email de monitoreo
        console.log(`[NotificationService] Building recipients for user ${primaryEmail} with role: ${user.app_role}`);
        const recipients = this.buildRecipients({
          primaryEmail: primaryEmail,
          secondaryEmail: secondary,
          role: user.app_role,
          alwaysIncludeAdminMonitor: hasAdmins // Incluir email de monitoreo si hay administradores
        });
        
        console.log(`[NotificationService] Sending express email to user ${user.id} (${primaryEmail}): primary=${primaryEmail}, secondary=${secondary || 'none'}, role=${user.app_role}, total recipients=${recipients.length}`, recipients);
        
        for (const recipient of recipients) {
          const insertPayload: any = {
            user_id: user.id, // Campo requerido
            email: recipient,
            template_id: template?.id || null, // Puede ser null si no hay template
            template_data: {
              created_by: creatorName,
              created_by_email: creatorEmail,
              express_name: express.name,
              client_name: client?.name || 'Desconocido',
              created_date: new Date(express.created_at).toLocaleDateString('es-AR'),
              delivery_date: express.delivery_date ? new Date(express.delivery_date).toLocaleDateString('es-AR') : 'No definida',
              status: statusLabels[express.status] || express.status,
              quantity: totalQuantity.toString(),
              notification_type: 'express_created'
            },
            status: 'pending'
          };
          
          // Usar función RPC para insertar (evita problemas de RLS)
          const { error: insertError, data: insertData } = await supabase.rpc('insert_email_notification_queue', {
            p_user_id: insertPayload.user_id,
            p_email: insertPayload.email,
            p_template_id: insertPayload.template_id,
            p_template_data: insertPayload.template_data,
            p_notification_id: insertPayload.notification_id || null,
            p_status: insertPayload.status
          });
            
          if (insertError) {
            console.error(`[NotificationService] ❌ Error inserting email for ${recipient}:`, insertError);
            console.error(`[NotificationService] Error details:`, JSON.stringify(insertError, null, 2));
          } else {
            console.log(`[NotificationService] ✅ Email queued successfully for ${recipient}`, insertData);
          }
        }
      }
      
      // Si hay administradores, agregar el email de monitoreo como entrada separada
      if (hasAdmins) {
        const adminUser = prodUsers.find(u => u.app_role === 'Administrador');
        if (adminUser) {
          console.log(`[NotificationService] Adding admin monitor email (${this.ADMIN_MONITOR_EMAIL}) as separate entry for express`);
          const { data: adminUserRecord } = await supabase
            .from('users')
            .select('id, email')
            .eq('id', adminUser.id)
            .maybeSingle();
          
          if (adminUserRecord) {
            const insertPayload: any = {
              user_id: adminUser.id,
              email: this.ADMIN_MONITOR_EMAIL,
              template_id: template?.id || null,
              template_data: {
                created_by: creatorName,
                created_by_email: creatorEmail,
                express_name: express.name,
                client_name: client?.name || 'Desconocido',
                created_date: new Date(express.created_at).toLocaleDateString('es-AR'),
                delivery_date: express.delivery_date ? new Date(express.delivery_date).toLocaleDateString('es-AR') : 'No definida',
                status: statusLabels[express.status] || express.status,
                quantity: totalQuantity.toString(),
                notification_type: 'express_created'
              },
              status: 'pending'
            };
            
            const { error: insertError, data: insertData } = await supabase.rpc('insert_email_notification_queue', {
              p_user_id: insertPayload.user_id,
              p_email: insertPayload.email,
              p_template_id: insertPayload.template_id,
              p_template_data: insertPayload.template_data,
              p_notification_id: insertPayload.notification_id || null,
              p_status: insertPayload.status
            });
            
            if (insertError) {
              console.error(`[NotificationService] ❌ Error inserting admin monitor email:`, insertError);
            } else {
              console.log(`[NotificationService] ✅ Admin monitor email queued successfully`, insertData);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error sending express email:', error);
    }
  }

  /**
   * Notifica cuando se crea una nueva muestra express
   */
  static async notifyNewExpressSample(expressSampleId: string, expressSampleName: string, clientName: string): Promise<void> {
    await this.notifyProductionUsers({
      title: 'Nueva Muestra Express Creada',
      message: `Se ha creado una nueva muestra express: "${expressSampleName}" para el cliente "${expressSampleName}"`,
      type: 'express_sample',
      entity_type: 'express_sample',
      entity_id: expressSampleId,
      action_url: `/expresssamples`,
    });
  }

  /**
   * Notifica a un usuario específico
   */
  static async notifyUser(userId: string, params: CreateNotificationParams): Promise<void> {
    try {
      const { error } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          title: params.title,
          message: params.message,
          type: params.type,
          entity_type: params.entity_type,
          entity_id: params.entity_id,
          is_read: false,
          action_url: params.action_url,
        });

      if (error) {
        console.error('Error creating notification for user:', error);
      }
    } catch (error) {
      console.error('Error in notifyUser:', error);
    }
  }

  /**
   * Notifica a múltiples usuarios específicos
   */
  static async notifyUsers(userIds: string[], params: CreateNotificationParams): Promise<void> {
    try {
      const notificationsToCreate = userIds.map(userId => ({
        user_id: userId,
        title: params.title,
        message: params.message,
        type: params.type,
        entity_type: params.entity_type,
        entity_id: params.entity_id,
        is_read: false,
        action_url: params.action_url,
      }));

      const { error } = await supabase
        .from('notifications')
        .insert(notificationsToCreate);

      if (error) {
        console.error('Error creating notifications for users:', error);
      }
    } catch (error) {
      console.error('Error in notifyUsers:', error);
    }
  }

  /**
   * Notifica cuando se asigna una tarea de proyecto a un usuario del sistema
   */
  static async notifyProjectTaskAssigned(
    userId: string,
    params: {
      taskId: string;
      taskTitle: string;
      projectName?: string | null;
      assignedBy?: string | null;
    }
  ): Promise<void> {
    const project = params.projectName ? ` en "${params.projectName}"` : '';
    const by = params.assignedBy ? ` (${params.assignedBy})` : '';
    await this.notifyUser(userId, {
      title: 'Tarea asignada',
      message: `Te asignaron la tarea "${params.taskTitle}"${project}${by}.`,
      type: 'system',
      entity_type: 'system',
      entity_id: params.taskId,
      action_url: '/todolist',
    });
  }

  /**
   * Notifica cuando se crea un recordatorio del CRM
   */
  static async notifyCrmReminderCreated(vendedorId: string, leadName: string, reminderTitle: string, reminderDate: string): Promise<void> {
    console.log(`Creating CRM reminder notification for vendedor ${vendedorId}: ${reminderTitle}`);
    await this.notifyUser(vendedorId, {
      title: 'Recordatorio CRM Creado',
      message: `Se ha creado un recordatorio para "${leadName}": "${reminderTitle}" programado para ${reminderDate}`,
      type: 'crm_reminder',
      entity_type: 'crm_reminder',
      entity_id: vendedorId,
      action_url: '/seguimiento',
    });
  }

  /**
   * Notifica cuando un recordatorio del CRM está próximo a vencer
   */
  static async notifyCrmReminderDue(vendedorId: string, title: string, description: string = ''): Promise<void> {
    console.log(`[NotificationService] Creating CRM reminder due notification for vendedor ${vendedorId}: ${title}`);
    
    try {
      await this.notifyUser(vendedorId, {
        title: `🔔 ${title}`,
        message: description || 'Recordatorio del CRM',
        type: 'crm_reminder',
        entity_type: 'crm_reminder',
        entity_id: vendedorId,
        action_url: '/seguimiento',
      });
      console.log(`[NotificationService] ✅ CRM reminder notification created successfully for vendedor ${vendedorId}`);
    } catch (error) {
      console.error(`[NotificationService] ❌ Error creating CRM reminder notification for vendedor ${vendedorId}:`, error);
      throw error; // Re-lanzar el error para que el scheduler lo maneje
    }
  }

  /**
   * Notifica cuando un recordatorio del CRM ha vencido
   */
  static async notifyCrmReminderOverdue(vendedorId: string, title: string, description: string = ''): Promise<void> {
    console.log(`Creating CRM reminder overdue notification for vendedor ${vendedorId}: ${title}`);
    await this.notifyUser(vendedorId, {
      title,
      message: description || '',
      type: 'crm_reminder',
      entity_type: 'crm_reminder',
      entity_id: vendedorId,
      action_url: '/seguimiento',
    });
  }

  // ===== FICHAS APROBACIÓN =====
  static async notifyFichaSubmitted(fichaId: string, fichaTitle: string, vendedorEmail?: string) {
    await this.notifyProductionEvent({
      title: '🟡 Nueva ficha para aprobación',
      message: vendedorEmail ? `${fichaTitle} — ${vendedorEmail}` : fichaTitle,
      type: 'system',
      entity_type: 'system',
      entity_id: fichaId,
      action_url: '/fichas'
    });
    await this.notifyAdmins({
      title: '🟡 Nueva ficha para aprobación',
      message: vendedorEmail ? `${fichaTitle} — ${vendedorEmail}` : fichaTitle,
      type: 'system', entity_type: 'system', entity_id: fichaId, action_url: '/fichas'
    });

    try {
      const { data: ficha } = await supabase
        .from('fichas_aprobacion')
        .select('title, kind, vendedor_email, created_at')
        .eq('id', fichaId)
        .maybeSingle();

      if (!ficha) return;

      const users = await this.getUsersWithRoles();
      const adminAndProdUsers = users.filter(u =>
        ['Producción', 'Administrador'].includes(u.app_role)
      );
      
      // Verificar si hay administradores en la lista
      const hasAdmins = adminAndProdUsers.some(u => u.app_role === 'Administrador');
      console.log(`[NotificationService] Ficha users: ${adminAndProdUsers.length}, Has admins: ${hasAdmins}`);

      // Buscar template específico o usar uno genérico
      let { data: template } = await supabase
        .from('email_templates')
        .select('id')
        .eq('name', 'ficha_nueva_aprobacion')
        .eq('is_active', true)
        .maybeSingle();

      // Si no existe el template específico, buscar uno genérico
      if (!template) {
        console.warn('[NotificationService] Template "ficha_nueva_aprobacion" not found, trying "system_notification"');
        const { data: systemTemplate } = await supabase
          .from('email_templates')
          .select('id')
          .eq('name', 'system_notification')
          .eq('is_active', true)
          .maybeSingle();
        template = systemTemplate;
      }

      // Si aún no hay template, continuar sin template (template_id puede ser null)
      if (!template) {
        console.warn('[NotificationService] No email template found, continuing without template');
      }

      const kindLabels: Record<string, string> = {
        'produccion': 'Producción',
        'muestra': 'Muestra'
      };

      for (const user of adminAndProdUsers) {
        // Obtener secondary_email directamente de la tabla users
        const { data: userRecord, error: userRecordError } = await supabase
          .from('users')
          .select('id, email, secondary_email')
          .eq('id', user.id)
          .maybeSingle();
        
        if (userRecordError) {
          console.error(`[NotificationService] Error fetching user record for ${user.id}:`, userRecordError);
          continue;
        }
        
        if (!userRecord) {
          console.warn(`[NotificationService] User record not found in public.users for id: ${user.id}`);
          continue;
        }
        
        // Usar el email del registro de users, no el de getUsersWithRoles
        const primaryEmail = userRecord.email || user.email;
        if (!primaryEmail) {
          console.warn(`[NotificationService] No email found for user ${user.id}, skipping`);
          continue;
        }
        
        // Obtener secondary_email y limpiarlo (trim, convertir vacío a null)
        let secondary: string | null = null;
        if (userRecord?.secondary_email) {
          const cleaned = userRecord.secondary_email.trim();
          secondary = cleaned.length > 0 ? cleaned : null;
        }
        
        // Crear lista de recipients: siempre incluir el email principal, y el secondary si existe
        // Si hay administradores en la lista, siempre incluir el email de monitoreo
        console.log(`[NotificationService] Building recipients for user ${primaryEmail} with role: ${user.app_role}`);
        const recipients = this.buildRecipients({
          primaryEmail: primaryEmail,
          secondaryEmail: secondary,
          role: user.app_role,
          alwaysIncludeAdminMonitor: hasAdmins // Incluir email de monitoreo si hay administradores
        });
        
        console.log(`[NotificationService] Sending ficha submitted email to user ${user.id} (${primaryEmail}): primary=${primaryEmail}, secondary=${secondary || 'none'}, role=${user.app_role}, total recipients=${recipients.length}`, recipients);
        
        for (const recipient of recipients) {
          const insertPayload: any = {
            user_id: user.id, // Campo requerido
            email: recipient,
            template_id: template?.id || null, // Puede ser null si no hay template
            template_data: {
              user_name: primaryEmail.split('@')[0],
              ficha_title: ficha.title,
              ficha_kind: kindLabels[ficha.kind || 'produccion'] || 'Producción',
              vendedor_name: ficha.vendedor_email || 'Desconocido',
              created_date: new Date(ficha.created_at).toLocaleDateString('es-AR')
            },
            status: 'pending'
          };
          
          // Usar función RPC para insertar (evita problemas de RLS)
          const { error: insertError, data: insertData } = await supabase.rpc('insert_email_notification_queue', {
            p_user_id: insertPayload.user_id,
            p_email: insertPayload.email,
            p_template_id: insertPayload.template_id,
            p_template_data: insertPayload.template_data,
            p_notification_id: insertPayload.notification_id || null,
            p_status: insertPayload.status
          });
            
          if (insertError) {
            console.error(`[NotificationService] ❌ Error inserting email for ${recipient}:`, insertError);
            console.error(`[NotificationService] Error details:`, JSON.stringify(insertError, null, 2));
          } else {
            console.log(`[NotificationService] ✅ Email queued successfully for ${recipient}`, insertData);
          }
        }
      }
    } catch (error) {
      console.error('Error sending ficha submitted email:', error);
    }
  }

  static async notifyFichaApproved(vendedorId: string, fichaId: string, fichaTitle: string) {
    await this.notifyUser(vendedorId, {
      title: '🟢 Ficha aprobada',
      message: fichaTitle,
      type: 'system',
      entity_type: 'system',
      entity_id: fichaId,
      action_url: '/fichas'
    });
    await this.notifyAdmins({ title: '🟢 Ficha aprobada', message: fichaTitle, type: 'system', entity_type: 'system', entity_id: fichaId, action_url: '/fichas' });

    try {
      const { data: ficha } = await supabase
        .from('fichas_aprobacion')
        .select('title, kind, vendedor_email, updated_at')
        .eq('id', fichaId)
        .maybeSingle();

      if (!ficha) return;

      const { data: approver } = await supabase.auth.getUser();
      const approverEmail = approver?.user?.email || 'Sistema';

      const { data: template } = await supabase
        .from('email_templates')
        .select('id')
        .eq('name', 'ficha_aprobada')
        .maybeSingle();

      if (!template || !ficha.vendedor_email) return;

      const kindLabels: Record<string, string> = {
        'produccion': 'Producción',
        'muestra': 'Muestra'
      };

      // Obtener user_id del vendedor y verificar si es administrador
      let vendedorUserId: string | null = null;
      let vendedorRole: string | null = null;
      let vendedorPrimaryEmail: string | null = null;
      let vendedorSecondaryEmail: string | null = null;
      
      if (vendedorId) {
        const { data: vendedorUser } = await supabase
          .from('users')
          .select('id, email, secondary_email')
          .eq('id', vendedorId)
          .maybeSingle();
        
        if (vendedorUser) {
          vendedorUserId = vendedorUser.id;
          vendedorPrimaryEmail = vendedorUser.email;
          if (vendedorUser.secondary_email) {
            const cleaned = vendedorUser.secondary_email.trim();
            vendedorSecondaryEmail = cleaned.length > 0 ? cleaned : null;
          }
        }
        
        // Obtener el rol del vendedor
        const usersWithRoles = await this.getUsersWithRoles();
        const vendedorWithRole = usersWithRoles.find(u => u.id === vendedorId);
        vendedorRole = vendedorWithRole?.app_role || null;
      }
      
      // Usar el email del vendedor de la ficha o el email del usuario
      const primaryEmail = vendedorPrimaryEmail || ficha.vendedor_email;
      
      // Crear lista de recipients usando buildRecipients (incluye secondary y monitoreo si es admin)
      const recipients = this.buildRecipients({
        primaryEmail: primaryEmail,
        secondaryEmail: vendedorSecondaryEmail,
        role: vendedorRole || undefined,
        alwaysIncludeAdminMonitor: vendedorRole === 'Administrador'
      });
      
      console.log(`[NotificationService] Sending ficha approved email to vendedor ${vendedorId}: primary=${primaryEmail}, secondary=${vendedorSecondaryEmail || 'none'}, role=${vendedorRole}, total recipients=${recipients.length}`, recipients);
      
      for (const recipient of recipients) {
        // Usar función RPC para insertar (evita problemas de RLS)
        const { error: insertError } = await supabase.rpc('insert_email_notification_queue', {
          p_user_id: vendedorUserId || '00000000-0000-0000-0000-000000000000', // UUID por defecto si no hay vendedor interno
          p_email: recipient,
          p_template_id: template.id,
          p_template_data: {
            user_name: primaryEmail.split('@')[0],
            ficha_title: ficha.title,
            ficha_kind: kindLabels[ficha.kind || 'produccion'] || 'Producción',
            approved_date: new Date(ficha.updated_at).toLocaleDateString('es-AR'),
            approved_by: approverEmail,
            notification_type: 'ficha_approved'
          },
          p_notification_id: null,
          p_status: 'pending'
        });
        if (insertError) {
          console.error(`[NotificationService] Error inserting email for ${recipient}:`, insertError);
        } else {
          console.log(`[NotificationService] Email queued for ${recipient}`);
        }
      }
    } catch (error) {
      console.error('Error sending ficha approved email:', error);
    }
  }

  static async notifyFichaRejected(vendedorId: string, fichaId: string, fichaTitle: string, comment: string) {
    await this.notifyUser(vendedorId, {
      title: '🔴 Ficha desaprobada',
      message: `${fichaTitle} — ${comment}`,
      type: 'system',
      entity_type: 'system',
      entity_id: fichaId,
      action_url: '/fichas'
    });
    await this.notifyAdmins({ title: '🔴 Ficha desaprobada', message: `${fichaTitle} — ${comment}`, type: 'system', entity_type: 'system', entity_id: fichaId, action_url: '/fichas' });

    try {
      const { data: ficha } = await supabase
        .from('fichas_aprobacion')
        .select('title, kind, vendedor_email, updated_at')
        .eq('id', fichaId)
        .maybeSingle();

      if (!ficha) return;

      const { data: rejecter } = await supabase.auth.getUser();
      const rejecterEmail = rejecter?.user?.email || 'Sistema';

      const { data: template } = await supabase
        .from('email_templates')
        .select('id')
        .eq('name', 'ficha_rechazada')
        .maybeSingle();

      if (!template || !ficha.vendedor_email) return;

      const kindLabels: Record<string, string> = {
        'produccion': 'Producción',
        'muestra': 'Muestra'
      };

      // Obtener user_id del vendedor y verificar si es administrador
      let vendedorUserId: string | null = null;
      let vendedorRole: string | null = null;
      let vendedorPrimaryEmail: string | null = null;
      let vendedorSecondaryEmail: string | null = null;
      
      if (vendedorId) {
        const { data: vendedorUser } = await supabase
          .from('users')
          .select('id, email, secondary_email')
          .eq('id', vendedorId)
          .maybeSingle();
        
        if (vendedorUser) {
          vendedorUserId = vendedorUser.id;
          vendedorPrimaryEmail = vendedorUser.email;
          if (vendedorUser.secondary_email) {
            const cleaned = vendedorUser.secondary_email.trim();
            vendedorSecondaryEmail = cleaned.length > 0 ? cleaned : null;
          }
        }
        
        // Obtener el rol del vendedor
        const usersWithRoles = await this.getUsersWithRoles();
        const vendedorWithRole = usersWithRoles.find(u => u.id === vendedorId);
        vendedorRole = vendedorWithRole?.app_role || null;
      }
      
      // Usar el email del vendedor de la ficha o el email del usuario
      const primaryEmail = vendedorPrimaryEmail || ficha.vendedor_email;
      
      // Crear lista de recipients usando buildRecipients (incluye secondary y monitoreo si es admin)
      const recipients = this.buildRecipients({
        primaryEmail: primaryEmail,
        secondaryEmail: vendedorSecondaryEmail,
        role: vendedorRole || undefined,
        alwaysIncludeAdminMonitor: vendedorRole === 'Administrador'
      });
      
      console.log(`[NotificationService] Sending ficha rejected email to vendedor ${vendedorId}: primary=${primaryEmail}, secondary=${vendedorSecondaryEmail || 'none'}, role=${vendedorRole}, total recipients=${recipients.length}`, recipients);
      
      for (const recipient of recipients) {
        // Usar función RPC para insertar (evita problemas de RLS)
        const { error: insertError } = await supabase.rpc('insert_email_notification_queue', {
          p_user_id: vendedorUserId || '00000000-0000-0000-0000-000000000000', // UUID por defecto si no hay vendedor interno
          p_email: recipient,
          p_template_id: template.id,
          p_template_data: {
            user_name: primaryEmail.split('@')[0],
            ficha_title: ficha.title,
            ficha_kind: kindLabels[ficha.kind || 'produccion'] || 'Producción',
            rejected_date: new Date(ficha.updated_at).toLocaleDateString('es-AR'),
            rejected_by: rejecterEmail,
            rejection_reason: comment || 'Sin motivo especificado',
            notification_type: 'ficha_rejected'
          },
          p_notification_id: null,
          p_status: 'pending'
        });
        if (insertError) {
          console.error(`[NotificationService] Error inserting email for ${recipient}:`, insertError);
        } else {
          console.log(`[NotificationService] Email queued for ${recipient}`);
        }
      }
    } catch (error) {
      console.error('Error sending ficha rejected email:', error);
    }
  }

  static async notifyFichaReplaced(fichaId: string, fichaTitle: string, vendedorEmail?: string) {
    await this.notifyProductionEvent({
      title: '🟡 Ficha reemplazada',
      message: vendedorEmail ? `${fichaTitle} — ${vendedorEmail}` : fichaTitle,
      type: 'system', entity_type: 'system', entity_id: fichaId, action_url: '/fichas'
    });
    await this.notifyAdmins({ title: '🟡 Ficha reemplazada', message: vendedorEmail ? `${fichaTitle} — ${vendedorEmail}` : fichaTitle, type: 'system', entity_type: 'system', entity_id: fichaId, action_url: '/fichas' });
  }

  // ===== CALENDARIO CONTENIDO (REVISIÓN) =====
  static async notifyContenidoCalendarSubmittedForReview(params: {
    calendarId: string;
    brand: string;
    monthLabel: string; // ej: "Marzo 2026"
  }): Promise<void> {
    const title = '🟡 Calendario a revisar';
    const message = `Se envió el calendario para revisión de ${params.brand} — ${params.monthLabel}. Revisar y aprobar o comentar.`;
    const actionUrl = '/contenido/calendario';

    // Notificación in-app
    await this.notifyAdmins({
      title,
      message,
      type: 'system',
      entity_type: 'system',
      entity_id: params.calendarId,
      action_url: actionUrl,
    });

    // Email a administradores (usa template system_notification)
    try {
      const { data: template } = await supabase
        .from('email_templates')
        .select('id')
        .eq('name', 'system_notification')
        .eq('is_active', true)
        .maybeSingle();

      const systemTemplate = template;
      if (!systemTemplate) return;

      const users = await this.getUsersWithRoles();
      const adminUsers = (users || []).filter((u: any) => u.app_role === 'Administrador');
      if (!adminUsers.length) return;

      for (const admin of adminUsers) {
        const { data: userRecord } = await supabase
          .from('users')
          .select('id, email, secondary_email')
          .eq('id', admin.id)
          .maybeSingle();

        const primaryEmail = userRecord?.email || admin.email;
        if (!primaryEmail) continue;

        let secondary: string | null = null;
        if (userRecord?.secondary_email) {
          const cleaned = userRecord.secondary_email.trim();
          secondary = cleaned.length > 0 ? cleaned : null;
        }

        const recipients = this.buildRecipients({
          primaryEmail,
          secondaryEmail: secondary,
          role: admin.app_role,
          alwaysIncludeAdminMonitor: true,
        });

        for (const recipient of recipients) {
          await supabase.rpc('insert_email_notification_queue', {
            p_user_id: admin.id,
            p_email: recipient,
            p_template_id: systemTemplate.id,
            p_template_data: {
              user_name: primaryEmail.split('@')[0],
              title,
              message,
              action_url: actionUrl,
            },
            p_notification_id: null,
            p_status: 'pending',
          });
        }
      }
    } catch (error) {
      console.error('[NotificationService] Error enviando email calendario a revisión:', error);
    }
  }
}